import { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Context } from './Context';
import { Breadcrumb as NativeBreadcrumb } from '../components/native/Breadcrumb';
import { Breadcrumb as MuiBreadcrumb } from '../components/mui/Breadcrumb';
import { Breadcrumb as ShadcnBreadcrumb } from '../components/shadcn/Breadcrumb';
import { iconMap } from '../components/native/iconMap';
import { FontIcon } from '../components/mui/FontIcon';
import { LucideIcon } from '../components/shadcn/LucideIcon';

/**
 * Header - Application header bar
 * Displays breadcrumb on left, personal models on right
 */
export function Header() {
  const [context] = useContext(Context)!
  const location = useLocation();
  const navigate = useNavigate();
  const project = context.project;
  const design = project?.design ?? 'native';

  const personalModels = useMemo(() =>
    [...new Map(context.authn
      .filter(auth => auth.project?.id === context.project?.id)
      .map(auth => auth.capability.model)
      .map(model => [model.id, model])).values()]
      // Profile (personal → Users) is excluded — it links from the sidebar user avatar instead
      .filter(model => !model.hidden && model.type === 'personal' && model.reference?.path !== '/users'),
    [context.authn, context.project]);

  const renderIcon = (icon: string) => {
    if (design === 'shadcn') return <LucideIcon name={icon} size="small" />;
    if (design === 'mui') return <FontIcon name={icon} />;
    return <span>{iconMap[icon] || icon}</span>;
  };

  // Build breadcrumb from URL path
  const Breadcrumb = design === 'shadcn' ? ShadcnBreadcrumb : design === 'mui' ? MuiBreadcrumb : NativeBreadcrumb;
  const crumbs = location.pathname.split('/').filter(Boolean);

  return (
    <header style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '3.5rem',
      padding: '0 2rem',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
    }}>
      <Breadcrumb crumbs={crumbs} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {personalModels.map(model => (
          <span
            key={model.path}
            onClick={() => navigate(model.path)}
            title={model.name}
            style={{
              cursor: 'pointer',
              opacity: location.pathname === model.path ? 1 : 0.6,
            }}
          >
            {model.icon ? renderIcon(model.icon) : model.name}
          </span>
        ))}
      </div>
    </header>
  );
}
